import { Calculation } from '@carbon/react/icons';
import { BaseCard } from 'components/base-card';
import { CalculationIconTrigger } from 'components/calculation-icon-trigger';
import { CopyButton } from 'components/copy-button/copy-button';
import { FormulaDefinition, InterestRowKey, InterestColumnKey } from './types';
import { FormulaRenderer } from './formula-renderer';
import styles from './interest-formula-popup.module.scss';

// Row display labels
const ROW_LABELS: Record<InterestRowKey, string> = {
  executive: 'Executive',
  royalty: 'Royalty',
  bonus: 'Bonus',
  delay: 'Delay',
  leaseRoyaltyRate: 'Lease Royalty Rate',
  orri: 'ORRI',
  wi: 'WI',
  nri: 'NRI',
};

// Generate title for the formula popup
const getFormulaTitle = (
  rowKey: InterestRowKey,
  columnKey: InterestColumnKey
): string => {
  // For output columns (nma, nmaPostBurden, nra), use "Output X" format
  if (columnKey === 'nra') {
    return 'Output NRA';
  }
  if (columnKey === 'nma') {
    return 'Output NMA';
  }
  if (columnKey === 'nmaPostBurden') {
    return 'Output Post Burden NMA';
  }
  // For interest column, use row label
  return `${ROW_LABELS[rowKey]} Interest`;
};

interface FormulaContentProps {
  title: string;
  formula: FormulaDefinition;
}

const FormulaContent = ({ title, formula }: FormulaContentProps) => {
  return (
    <div className={styles.container}>
      <div className={styles.title}>{title}</div>
      <div className={styles.calculationContainer}>
        <span className={styles.valuesText}>{formula.copyText}</span>
        <CopyButton node={formula.copyText} />
      </div>
      <div className={styles.formulaContainer}>
        <FormulaRenderer formula={formula} />
      </div>
    </div>
  );
};

export interface InterestFormulaPopupProps {
  rowKey: InterestRowKey;
  columnKey: InterestColumnKey;
  formula: FormulaDefinition;
}

export const InterestFormulaPopup = ({
  rowKey,
  columnKey,
  formula,
}: InterestFormulaPopupProps) => {
  const title = getFormulaTitle(rowKey, columnKey);

  const renderTrigger = (active: boolean, toggle: () => void) => (
    <CalculationIconTrigger
      definition="Show Calculation"
      active={active}
      onClick={toggle}
    >
      <div className={styles.iconContainer}>
        <Calculation aria-hidden="true" role="img" />
      </div>
    </CalculationIconTrigger>
  );

  return (
    <BaseCard
      contentClassName={styles.cardContent}
      renderTrigger={renderTrigger}
      content={<FormulaContent title={title} formula={formula} />}
    />
  );
};
